import { SessionContext } from '@/context/SessionContext';
import {
	Anchor,
	Box,
	Button,
	Checkbox,
	Group,
	LoadingOverlay,
	Modal,
	PasswordInput,
	Stack,
	Text,
	TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconLock, IconUser } from '@tabler/icons-react';
import { useContext, useEffect, useState } from 'react';
import ForgotPasswordModal from './ForgotPasswordModal';
import RegistrationModal from './RegistrationModal';
import { useNavigate } from 'react-router';

interface LoginModalProps {
	showModal: boolean;
	setShowModal: (show: boolean) => void;
}

const LoginModal: React.FC<LoginModalProps> = (props) => {
	const session = useContext(SessionContext);
	if (session === undefined) {
		throw new Error('Modals must be a child of sessionContext.');
	}

	const navigate = useNavigate();

	const { showModal, setShowModal } = props;
	const [showRegistrationForm, setShowRegistrationForm] = useState(false);
	const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	// If the user is already signed in, then navigate out of the login modal
	useEffect(() => {
		if (session.user != null) {
			setShowModal(false);
		}
	}, [session.user, setShowModal]);

	const form = useForm({
		initialValues: {
			usernameOrEmail: '',
			password: '',
			rememberMe: true,
		},
	});

	const handleSubmit = async ({ usernameOrEmail, password, rememberMe }: typeof form.values) => {
		setIsSubmitting(true);
		if (!(await session.login(usernameOrEmail, password, rememberMe))) {
			setIsSubmitting(false);
		}
	};

	useEffect(() => {
		if (session.user != null)
		{
			setShowModal(false);
			setIsSubmitting(false);
		}
	}, [setShowModal, session.user]);

	// Treat both API submit and session bootstrap as "busy" for disabling controls
	const isBusy = isSubmitting || session.loadingUserInfo;

	return (
		<Modal
			opened={showModal}
			onClose={() => setShowModal(false)}
			title="Log In"
			centered
			size="md"
			styles={{
				title: { fontSize: 26, fontVariant: 'all-small-caps' }
			}}
			zIndex={100}
		>
			<RegistrationModal showModal={showRegistrationForm} setShowModal={setShowRegistrationForm} />
			<ForgotPasswordModal showModal={showForgotPasswordModal} setShowModal={setShowForgotPasswordModal} />

			{/* Wrap content in a positioned container so LoadingOverlay can cover it */}
			<Box pos="relative" aria-busy={session.loadingUserInfo}>
				<LoadingOverlay
					visible={session.loadingUserInfo}
					zIndex={200}
					loaderProps={{ size: 'xl' }}        // big loader
					overlayProps={{ blur: 0.5 }}          // subtle gray/blur effect
				/>

				<Stack>
					<form onSubmit={form.onSubmit(handleSubmit)}>
						<Stack>
							<TextInput
								label={
									<Group>
										<IconUser size={20} />
										<Text>Username (or Email Address)</Text>
									</Group>
								}
								disabled={isBusy}
								{...form.getInputProps('usernameOrEmail')}
								required
								withAsterisk={false}
							/>

							<PasswordInput
								label={
									<Group>
										<IconLock size={20} />
										<Text>Password</Text>
									</Group>
								}
								disabled={isBusy}
								{...form.getInputProps('password')}
								required
								withAsterisk={false}
							/>

							<Anchor
								component="button"
								type="button"
								onClick={() => setShowForgotPasswordModal(true)}
								size="s"
								disabled={isBusy}
							>
								Forgot password?
							</Anchor>

							<Checkbox
								label="Remember Me"
								{...form.getInputProps('rememberMe', { type: 'checkbox' })}
								disabled={isBusy}
							/>

							<Button
								type="submit"
								radius="xl"
								size="sm"
								loading={isSubmitting}
								disabled={session.loadingUserInfo}
								color="blue.7"
							>
								Log In
							</Button>
						</Stack>
					</form>

					<Stack align="center">
						<Anchor
							component="button"
							type="button"
							onClick={() => 
								navigate("register")
							}
							size="s"
							disabled={isBusy}
						>
							Don&apos;t have an account? Register here.
						</Anchor>
					</Stack>
				</Stack>
			</Box>
		</Modal>
	);
};

export default LoginModal;
